import { useEffect, useState } from 'react';
import { usePlan } from '../lib/plan';
import { api, type Share } from '../lib/api';
import { Icon } from '../lib/icons';
import { listTime, shortDate } from '../lib/format';
import { Dialog, Picker, useToast } from './ui';

// Externe per Link in eine Aufgabe oder ein Projekt einladen

const EXPIRY: { value: string; label: string }[] = [
  { value: '0', label: 'Unbegrenzt' },
  { value: '7', label: '7 Tage' },
  { value: '30', label: '30 Tage' },
  { value: '90', label: '90 Tage' },
];

export function ShareDialog({ target, title, onClose }: { target: { taskId: string } | { projectId: string }; title: string; onClose: () => void }) {
  const toast = useToast();
  const isTask = 'taskId' in target;
  const query = isTask ? `taskId=${target.taskId}` : `projectId=${target.projectId}`;
  const plan = usePlan();
  const [shares, setShares] = useState<Share[] | null>(null);
  const [label, setLabel] = useState('');
  const [canWrite, setCanWrite] = useState(true);
  const [expiry, setExpiry] = useState('0');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = () =>
    api<{ shares: Share[] }>(`/shares?${query}`)
      .then((r) => setShares(r.shares))
      .catch((err: Error) => (toast(err.message), setShares([])));

  useEffect(() => {
    void load();
  }, [query]);

  async function create() {
    if (!plan.more('activeShares')) return;
    setBusy(true);
    try {
      const r = await api<{ share: Share }>('/shares', {
        method: 'POST',
        json: { ...target, label: label.trim() || null, canWrite, expiresInDays: Number(expiry) || null },
      });
      setLabel('');
      await load();
      if (r.share.url) await copy(r.share.url);
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast('Link kopiert');
    } catch {
      toast('Kopieren nicht möglich, bitte „Senden“ nutzen');
    }
  }

  async function send(share: Share) {
    if (!share.url) return;
    const text = isTask ? `Hier kannst du in der Aufgabe „${title}“ mitschreiben:` : `Hier kannst du im Projekt „${title}“ mitarbeiten:`;
    if (navigator.share) {
      await navigator.share({ title: `Mach's: ${title}`, text, url: share.url }).catch(() => {});
    } else {
      await copy(share.url);
    }
  }

  async function revoke(share: Share) {
    // Zweimal tippen statt Bestätigungsdialog
    if (confirming !== share.id) return setConfirming(share.id);
    setConfirming(null);
    try {
      await api(`/shares/${share.id}`, { method: 'DELETE' });
      await load();
      toast('Freigabe beendet');
    } catch (err) {
      toast((err as Error).message);
    }
  }

  return (
    <Dialog title={isTask ? 'Externe einladen' : 'Projekt freigeben'} onClose={onClose}>
      <p className="-mt-1 mb-4 text-[14px] text-[var(--muted)]">
        {isTask
          ? 'Wer den Link hat, sieht nur diese Aufgabe und arbeitet ohne Konto im Browser mit.'
          : 'Wer den Link hat, sieht alle Aufgaben dieses Projekts, schreibt mit und kann neue Aufgaben anlegen. Ohne Konto, im Browser.'}
      </p>

      <div className="flex flex-col gap-3 rounded-2xl border border-[var(--line)] p-3">
        <label className="field">
          Für wen? (optional)
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="z. B. Frau Weber" maxLength={80} />
        </label>
        <div className="grid grid-cols-2 gap-3 max-[420px]:grid-cols-1">
          <Picker
            label="Rechte"
            value={canWrite ? 'schreiben' : 'lesen'}
            onChange={(v) => setCanWrite(v === 'schreiben')}
            options={[
              { value: 'schreiben', label: 'Mitschreiben' },
              { value: 'lesen', label: 'Nur ansehen' },
            ]}
          />
          <Picker label="Gültig" value={expiry} onChange={setExpiry} options={EXPIRY} />
        </div>
        <button className="btn btn-primary" onClick={() => void create()} disabled={busy}>
          <Icon name="link" size={18} />Link erstellen und kopieren
        </button>
      </div>

      <h3 className="mb-2 mt-5 text-[13px] font-semibold text-[var(--muted)]">Aktive Links</h3>
      {shares === null && <div className="spinner mx-auto my-4" />}
      {shares?.length === 0 && <p className="text-[14px] text-[var(--muted)]">Noch keine Freigabe.</p>}
      <ul className="flex flex-col gap-2">
        {shares?.map((s) => (
          <li key={s.id} className="rounded-2xl border border-[var(--line)] p-3">
            <div className="flex items-center gap-2">
              <Icon name={s.canWrite ? 'share' : 'eye'} size={17} />
              <b className="flex-1 truncate text-[14.5px]">{s.label || (s.canWrite ? 'Mitschreiben' : 'Nur ansehen')}</b>
              <span className="text-[12px] text-[var(--muted)]">{s.expiresAt ? `bis ${shortDate(s.expiresAt)}` : 'unbegrenzt'}</span>
            </div>
            <p className="mt-1 text-[12.5px] text-[var(--muted)]">
              {s.guests.length === 0
                ? 'Noch niemand beigetreten'
                : s.guests.map((g) => `${g.name}${g.lastSeenAt ? ` (${listTime(g.lastSeenAt)})` : ''}`).join(', ')}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {s.url && <button className="btn btn-ghost h-9 px-3" onClick={() => void copy(s.url!)}><Icon name="link" size={16} />Kopieren</button>}
              {s.url && <button className="btn btn-ghost h-9 px-3" onClick={() => void send(s)}><Icon name="send" size={16} />Senden</button>}
              <button className="btn btn-ghost h-9 px-3 text-[var(--problem)]" onClick={() => void revoke(s)}>
                <Icon name="x" size={16} />{confirming === s.id ? 'Wirklich beenden?' : 'Beenden'}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
